import React from 'react';
import { Link } from 'react-router-dom';
import {
  BookOpenIcon,
  DocumentIcon,
  FlagIcon,
  CalendarIcon,
  MapIcon,
  ChartBarIcon,
} from '@heroicons/react/24/outline';

const features = [
  {
    name: 'Courses',
    description: 'Organize your learning into courses, each with sections, logs, and flashcards.',
    icon: BookOpenIcon,
  },
  {
    name: 'Certificate Archive',
    description: 'Store and manage your certificates, transcripts, and achievements.',
    icon: DocumentIcon,
  },
  {
    name: 'Learning Goals',
    description: 'Set and track your learning goals and milestones.',
    icon: FlagIcon,
  },
  {
    name: 'Learning Calendar',
    description: 'Schedule and organize your learning sessions.',
    icon: CalendarIcon,
  },
  {
    name: 'Skill Map',
    description: 'Visualize your skills and track your progress.',
    icon: MapIcon,
  },
  {
    name: 'Analytics',
    description: 'Get insights into your learning patterns and progress.',
    icon: ChartBarIcon,
  },
];

export default function Landing() {
  return (
    <div className="min-h-screen bg-white dark:bg-gray-900">
      {/* Navigation */}
      <nav className="fixed w-full bg-white dark:bg-gray-800 shadow-sm z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16">
            <div className="flex items-center">
              <img src="/growault-logo.png" alt="Growault Logo" className="h-8 w-auto" />
            </div>
            <div className="flex items-center space-x-4">
              <Link
                to="/login"
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white"
              >
                Log in
              </Link>
              <Link
                to="/signup"
                className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700"
              >
                Sign up
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <div className="pt-24 pb-16 sm:pt-32 sm:pb-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl font-extrabold text-gray-900 dark:text-white sm:text-5xl sm:tracking-tight lg:text-6xl">
              Your Personal Learning Journey
            </h1>
            <p className="mt-6 max-w-2xl mx-auto text-xl text-gray-500 dark:text-gray-300">
              Track, archive, and visualize your learning progress. Set goals, manage certificates,
              and build your personal knowledge base.
            </p>
            <div className="mt-10 flex justify-center space-x-4">
              <Link
                to="/signup"
                className="px-8 py-3 text-base font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700"
              >
                Get Started
              </Link>
              <Link
                to="/about"
                className="px-8 py-3 text-base font-medium text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white"
              >
                Learn More
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Features Section */}
      <div className="py-16 bg-gray-50 dark:bg-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h2 className="text-3xl font-extrabold text-gray-900 dark:text-white">
              Everything you need to manage your learning
            </h2>
            <p className="mt-4 text-lg text-gray-500 dark:text-gray-300">
              A comprehensive suite of tools to help you track and optimize your learning journey.
            </p>
          </div>

          <div className="mt-16">
            <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-3">
              {features.map((feature) => (
                <div
                  key={feature.name}
                  className="bg-white dark:bg-gray-700 rounded-xl shadow-soft p-6"
                >
                  <div className="flex items-center">
                    <div className="p-2 rounded-lg bg-primary-100 dark:bg-primary-900">
                      <feature.icon className="w-6 h-6 text-primary-600 dark:text-primary-400" />
                    </div>
                    <h3 className="ml-4 text-lg font-medium text-gray-900 dark:text-white">
                      {feature.name}
                    </h3>
                  </div>
                  <p className="mt-4 text-gray-500 dark:text-gray-300">
                    {feature.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="bg-primary-600">
        <div className="max-w-7xl mx-auto py-12 px-4 sm:px-6 lg:py-16 lg:px-8 lg:flex lg:items-center lg:justify-between">
          <h2 className="text-3xl font-extrabold tracking-tight text-white sm:text-4xl">
            <span className="block">Ready to start your learning journey?</span>
            <span className="block text-primary-200">Sign up for free today.</span>
          </h2>
          <div className="mt-8 flex lg:mt-0 lg:flex-shrink-0">
            <div className="inline-flex rounded-md shadow">
              <Link
                to="/signup"
                className="inline-flex items-center justify-center px-5 py-3 border border-transparent text-base font-medium rounded-md text-primary-600 bg-white hover:bg-primary-50"
              >
                Get started
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-white dark:bg-gray-800">
        <div className="max-w-7xl mx-auto py-12 px-4 sm:px-6 lg:px-8">
          <div className="border-t border-gray-200 dark:border-gray-700 pt-8">
            <p className="text-center text-base text-gray-400">
              &copy; {new Date().getFullYear()} Growault. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
} 